import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import {
    selectCounts,
    selectFiles,
    selectFilesData,
    selectIsSuccess,
    selectSize,
} from '~/src/redux/documents/documents.selectors';
import { usePostDocumentUpload } from '~/src/repositories/document-upload/hooks/use-post-document-upload.hook';
import DropzoneUploader from '~/src/components/common-components/dropzone-uploader/dropzone-uploader';
import { dropDocuments } from '~/src/redux/documents/documents-async-action-creator';
import {
    deleteDocument,
    resetDocument,
    setFileUploadStatus,
    setFileUploadData,
    subtractSizeForField,
} from '~/src/redux/documents/document.slice';
import { FormNameDocumentEnum } from '~/src/models/enums/form-name.enum';
import type { RootState } from '~/src/redux/store';
import type {
    DropzoneUploaderType,
} from '~/src/components/common-components/dropzone-uploader-decorator/dropzone-uploader-decorator.types';
import type { TypeFilesEnum } from '~/src/models/enums/type-files.enum';
import type {
    AddDocumentsPayloadInterface,
} from '~/src/components/common-components/dropzone-uploader/dropzone-uploader.types';
import type { FileDataInterface } from '~/src/redux/documents/documents.types';

export const DropzoneUploaderDecorator: FC<DropzoneUploaderType> = ({
    children,
    formName = FormNameDocumentEnum.agents,
    setFileUploadData: onUploadFileData,
    ...props
}) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const urlPostFileMethod = 'uploads';
    const { mutateAsync: postDocumentUpload, reset, progress } = usePostDocumentUpload(urlPostFileMethod, formName);

    const getSizeAll = useSelector((state: RootState) => (typeField: TypeFilesEnum) => selectSize(state, typeField));
    const getCount = useSelector((state: RootState) => (typeField: TypeFilesEnum) => selectCounts(state, typeField));
    const getFilesData = useSelector(
        (state: RootState) => (typeField: TypeFilesEnum) => selectFilesData(state, typeField),
    );

    const getIsSuccess = useSelector(
        (state: RootState) => (typeField: TypeFilesEnum) => selectIsSuccess(state, typeField),
    );

    const getFilesFromStore = useSelector(
        (state: RootState) => (typeField: TypeFilesEnum) => selectFiles(state, typeField),
    );

    const resetDocumentHandler = (typeField: TypeFilesEnum) => {
        dispatch(resetDocument({ typeField }));
    };

    const dropDocumentsHandler = ({ blobUrl, name, type, typeField, size }
            :AddDocumentsPayloadInterface<TypeFilesEnum>) => {
        dispatch(dropDocuments({ blobUrl, name, type, typeField, size }));
    };
    const setFileUploadStatusHandler = ({ isSuccess, typeField }: { isSuccess: boolean, typeField: TypeFilesEnum }) => {
        dispatch(setFileUploadStatus({ isSuccess, typeField }));
    };

    const deleteDocumentHandler = ({ typeField, name }: { name: string, typeField: TypeFilesEnum }) => {
        dispatch(deleteDocument({ typeField, name }));
    };

    const subtractSizeForFieldHandler = ({ typeField, size }: { size: number, typeField: TypeFilesEnum }) => {
        dispatch(subtractSizeForField({ typeField, size }));
    };

    const setFileUploadDataHandler = ({ typeField, data }:
        { data: { data: FileDataInterface }, typeField: TypeFilesEnum }) => {
        onUploadFileData({ typeField, data: { data } });
        dispatch(setFileUploadData({ typeField, data: data.data }));
    };

    return (
        <DropzoneUploader
            queryClient={queryClient}
            getSizeAll={getSizeAll}
            getCount={getCount}
            getFilesData={getFilesData}
            getIsSuccess={getIsSuccess}
            resetDocument={resetDocumentHandler}
            getFilesFromStore={getFilesFromStore}
            postDocumentUpload={postDocumentUpload}
            progress={progress}
            reset={reset}
            dropDocuments={dropDocumentsHandler}
            setFileUploadStatus={setFileUploadStatusHandler}
            deleteDocument={deleteDocumentHandler}
            subtractSizeForField={subtractSizeForFieldHandler}
            setFileUploadData={setFileUploadDataHandler}
            {...props}
        >
            {children}
        </DropzoneUploader>
    );
};
